/**
 * FR-018.7 — a client guard over the dry-run preview: every live row the batch would supersede must
 * be one the editor knowingly restates or replaces. The server's F-16 supersedes by
 * (system, kind, label), not by target, so a row the editor did not know about (another field's
 * disjoint season, a row written since the page loaded) would otherwise be replaced silently and its
 * seasons moved. Ids only — never a label.
 */
import type { PreviewResponse, SupersededTarget } from '../../../../services/sourcesService';

const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/**
 * The superseded ids (`supersedes` ∪ `also_supersedes` of every item) not in `accounted`, once each.
 * A superseded `proposed` row is never unexpected: a confirm restating a suggestion answers it, and
 * answering a proposal moves no data — on any confirm path. Nor is an `identical` row: a confirmed row
 * the batch restates verbatim (same target and seasons) moves nothing either.
 */
export function unexpectedSupersedes(preview: PreviewResponse, accounted: ReadonlySet<string>): string[] {
    const answered = new Set(
        (preview.superseded_targets ?? [])
            .filter((t) => t.status === 'proposed' || t.identical === true)
            .map((t) => t.mapping_id),
    );
    const out: string[] = [];
    for (const item of preview.items ?? []) {
        for (const id of [...ids(item.supersedes), ...ids(item.also_supersedes)]) {
            if (!accounted.has(id) && !answered.has(id) && !out.includes(id)) out.push(id);
        }
    }
    return out;
}

export interface UnrestatedOwners {
    /** Other objects holding an unrestated row of this label — one entry per object. */
    elsewhere: SupersededTarget[];
    /** An unrestated row targets THIS object (a row the editor does not restate). */
    here: boolean;
    /** One of those rows provably starts before the season of the item superseding it ("frühere Jahre"). */
    hereEarlier: boolean;
    /** An unrestated id the preview names no target for (an older backend). */
    unknown: boolean;
}

/** Whose rows the unrestated supersedes are (R2), so the editor can say where to change the name. */
export function unrestatedOwners(
    preview: PreviewResponse,
    accounted: ReadonlySet<string>,
    self: { entityType: string; entityId: string },
): UnrestatedOwners {
    const byId = new Map((preview.superseded_targets ?? []).map((t) => [t.mapping_id, t]));
    const out: UnrestatedOwners = { elsewhere: [], here: false, hereEarlier: false, unknown: false };
    for (const id of unexpectedSupersedes(preview, accounted)) {
        const target = byId.get(id);
        if (!target || !target.target_ref) {
            out.unknown = true;
        } else if (target.target_type === self.entityType && target.target_ref === self.entityId) {
            out.here = true;
            if (startsEarlier(preview, id, target.valid_from)) out.hereEarlier = true;
        } else if (!out.elsewhere.some((o) => o.target_type === target.target_type && o.target_ref === target.target_ref)) {
            out.elsewhere.push(target);
        }
    }
    return out;
}

/**
 * Whether a superseded row's range starts before the `valid_from` of an item superseding it — exact, so
 * "frühere Jahre" is never said of a row that merely overlaps. An open start (`null`) reaches back; an
 * unknown start (an older backend) or an open-start item is never "earlier".
 */
function startsEarlier(preview: PreviewResponse, id: string, rowFrom: number | null | undefined): boolean {
    if (rowFrom === undefined) return false;
    return (preview.items ?? []).some((item) => {
        if (![...ids(item.supersedes), ...ids(item.also_supersedes)].includes(id)) return false;
        const itemFrom = item.valid_from;
        return typeof itemFrom === 'number' && (rowFrom === null || rowFrom < itemFrom);
    });
}
